import React from 'react';
import { Box, Card, Chip, Grid, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import type { AttributionSourceStats, BookingAbandonmentStats, GrowthSummary } from '../../models/Growth';

const CARD_ACCENTS = {
  user: '#818cf8',
  host: '#34d399',
  tutor: '#c084fc',
  room: '#22d3ee',
  tutorBooking: '#2dd4bf',
  roomDropOff: '#fb923c',
  tutorDropOff: '#f472b6',
} as const;

function channelColor(channel: string): string {
  const key = channel.trim().toLowerCase();
  if (key === 'untracked' || key === 'unknown' || key === '(not set)') return '#94a3b8';
  if (key === 'direct' || key === '(direct)') return '#60a5fa';
  if (key === 'social' || key === 'organic social' || key === 'paid social') return '#f472b6';
  if (key.includes('paid') || key.includes('cpc') || key.includes('search')) return '#fbbf24';
  if (key.includes('organic')) return '#34d399';
  if (key.includes('referral')) return '#c084fc';
  if (key.includes('email')) return '#22d3ee';
  return '#a78bfa';
}

function sortedSources(rows: AttributionSourceStats[] | undefined): AttributionSourceStats[] {
  return [...(rows ?? [])].sort((a, b) => b.count - a.count || a.source.localeCompare(b.source));
}

function sameLabel(left: string, right: string): boolean {
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

function SourceCard({
  title,
  rows,
  accent,
}: {
  title: string;
  rows: AttributionSourceStats[] | undefined;
  accent: string;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const sorted = sortedSources(rows);
  const total = sorted.reduce((sum, row) => sum + row.count, 0);

  return (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        height: '100%',
        borderRadius: 2,
        borderColor: alpha(accent, 0.45),
        bgcolor: alpha(accent, theme.palette.mode === 'dark' ? 0.08 : 0.04),
        backgroundImage: `linear-gradient(180deg, ${alpha(accent, 0.22)} 0, ${alpha(accent, 0.22)} 3px, transparent 3px)`,
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
          {title}
        </Typography>
        <Chip
          size="small"
          label={total}
          sx={{
            fontWeight: 800,
            bgcolor: alpha(accent, 0.22),
            color: theme.palette.mode === 'dark' ? accent : theme.palette.text.primary,
            border: 1,
            borderColor: alpha(accent, 0.4),
          }}
        />
      </Stack>

      {sorted.length === 0 ? (
        <Typography color="text.secondary" sx={{ mt: 2 }}>
          {t('growth.noData', 'No data available')}
        </Typography>
      ) : (
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {sorted.map((row) => {
            const color = channelColor(row.channel);
            const share = total > 0 ? Math.round((row.count / total) * 100) : 0;
            const showShare = sorted.length > 1;
            const showChannel = !sameLabel(row.channel, row.source);
            return (
              <Box key={`${row.source}-${row.channel}`}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                  <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
                    {row.source}
                  </Typography>
                  {showChannel ? (
                    <Chip
                      size="small"
                      label={row.channel}
                      sx={{
                        height: 22,
                        maxWidth: 140,
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        bgcolor: alpha(color, 0.16),
                        color: theme.palette.mode === 'dark' ? color : theme.palette.text.primary,
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                  ) : null}
                  {showShare ? (
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 32, textAlign: 'right' }}>
                      {share}%
                    </Typography>
                  ) : null}
                  <Typography variant="body2" fontWeight={800} sx={{ minWidth: 24, textAlign: 'right' }}>
                    {row.count}
                  </Typography>
                </Stack>
                {showShare ? (
                  <Box
                    sx={{
                      mt: 0.75,
                      ml: 2,
                      height: 6,
                      borderRadius: 99,
                      bgcolor: alpha(color, 0.16),
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        width: `${row.count > 0 ? Math.max(share, 8) : 0}%`,
                        height: '100%',
                        bgcolor: color,
                      }}
                    />
                  </Box>
                ) : null}
              </Box>
            );
          })}
        </Stack>
      )}
    </Card>
  );
}

function DropOffCard({
  title,
  rows,
  accent,
}: {
  title: string;
  rows: BookingAbandonmentStats[];
  accent: string;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const sorted = [...rows].sort((a, b) => b.count - a.count || (a.stepIndex ?? 0) - (b.stepIndex ?? 0));
  const max = sorted.reduce((peak, row) => Math.max(peak, row.count), 0);

  return (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        height: '100%',
        borderRadius: 2,
        borderColor: alpha(accent, 0.45),
        bgcolor: alpha(accent, theme.palette.mode === 'dark' ? 0.08 : 0.04),
        backgroundImage: `linear-gradient(180deg, ${alpha(accent, 0.28)} 0, ${alpha(accent, 0.28)} 3px, transparent 3px)`,
      }}
    >
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {t('growth.dropOffScope', 'Tracked explicit web closes; counts are events, not unique users.')}
      </Typography>
      {sorted.length === 0 ? (
        <Typography color="text.secondary" sx={{ mt: 1.5 }}>
          {t('growth.noTrackedDropOff', 'No tracked drop-offs yet')}
        </Typography>
      ) : (
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {sorted.map((row) => {
            const share = max > 0 ? Math.round((row.count / max) * 100) : 0;
            return (
              <Box key={`${row.step}-${row.stepIndex}`}>
                <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1}>
                  <Typography variant="body2" fontWeight={600}>
                    {row.step}
                  </Typography>
                  <Typography variant="body2" fontWeight={800} sx={{ color: accent }}>
                    {row.count}
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    mt: 0.75,
                    height: 8,
                    borderRadius: 99,
                    bgcolor: alpha(accent, 0.16),
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ width: `${share}%`, height: '100%', bgcolor: accent, borderRadius: 99 }} />
                </Box>
              </Box>
            );
          })}
        </Stack>
      )}
    </Card>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="overline"
      color="text.secondary"
      sx={{ display: 'block', fontWeight: 800, letterSpacing: '0.08em', lineHeight: 1.2 }}
    >
      {children}
    </Typography>
  );
}

export default function GrowthAnalyticsPanels({ data }: { data: GrowthSummary }) {
  const { t } = useTranslation();

  const sourceCards = [
    {
      title: t('growth.userRegistrationSources', 'User registration sources'),
      rows: data.userRegistrationSources,
      accent: CARD_ACCENTS.user,
    },
    {
      title: t('growth.hostRegistrationSources', 'Host registration sources'),
      rows: data.hostRegistrationSources,
      accent: CARD_ACCENTS.host,
    },
    {
      title: t('growth.tutorRegistrationSources', 'Tutor registration sources'),
      rows: data.tutorRegistrationSources,
      accent: CARD_ACCENTS.tutor,
    },
  ] as const;

  const bookingCards = [
    {
      title: t('growth.roomBookingSources', 'Room booking sources'),
      rows: data.roomBookingSources,
      accent: CARD_ACCENTS.room,
    },
    {
      title: t('growth.tutorBookingSources', 'Tutor booking sources'),
      rows: data.tutorBookingSources,
      accent: CARD_ACCENTS.tutorBooking,
    },
  ] as const;

  return (
    <Grid container spacing={2.5}>
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom>
          {t('growth.analytics', 'Analytics')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(
            'growth.analyticsDescription',
            'Acquisition sources and explicitly tracked web booking drop-offs. Historic missing attribution is shown as Untracked; drop-off collection starts from rollout.',
          )}
        </Typography>
      </Grid>

      <Grid item xs={12} md={4}>
        <Stack spacing={2}>
          <SectionLabel>{t('growth.registrationSourcesSection', 'Registrations')}</SectionLabel>
          {sourceCards.map((card) => (
            <SourceCard key={card.title} title={card.title} rows={card.rows} accent={card.accent} />
          ))}
        </Stack>
      </Grid>

      <Grid item xs={12} md={4}>
        <Stack spacing={2}>
          <SectionLabel>{t('growth.bookingSourcesSection', 'Bookings')}</SectionLabel>
          {bookingCards.map((card) => (
            <SourceCard key={card.title} title={card.title} rows={card.rows} accent={card.accent} />
          ))}
        </Stack>
      </Grid>

      <Grid item xs={12} md={4}>
        <Stack spacing={2}>
          <SectionLabel>{t('growth.dropOffSection', 'Where bookings stop')}</SectionLabel>
          {(['space', 'tutor'] as const).map((providerType) => {
            const rows = (data.bookingAbandonments ?? []).filter((row) => row.providerType === providerType);
            const isRoom = providerType === 'space';
            return (
              <DropOffCard
                key={providerType}
                title={
                  isRoom
                    ? t('growth.roomBookingDropOff', 'Room booking stopped at step')
                    : t('growth.tutorBookingDropOff', 'Tutor booking stopped at step')
                }
                rows={rows}
                accent={isRoom ? CARD_ACCENTS.roomDropOff : CARD_ACCENTS.tutorDropOff}
              />
            );
          })}
        </Stack>
      </Grid>
    </Grid>
  );
}
